import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, History, Loader2, LogIn, MousePointerClick, PencilLine, RefreshCw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, PillTabs } from '../../../components/design-system/admin';
import { useFeatureUsageApi } from '../../../common/hooks/use-feature-usage-api.hook';
import type { ActivityKind, VendorActivityItem, VendorPerson } from '../../../common/models/feature-usage.model';
import { describeAction } from '../../support-access/describe-action';
import { byDay, TimelineLine, toLines } from './vendor-timeline';
import { vendorPageKey } from './vendor-pages';

type Filter = 'all' | ActivityKind;
const FILTERS: Filter[] = ['all', 'change', 'signin', 'page', 'credits'];
const PAGE_SIZE = 60;
const EVERYONE = '__everyone__';
const SHOWN_PAGES = 4;

const ICON = { change: PencilLine, signin: LogIn, pages: MousePointerClick, credits: Coins } as const;

/**
 * What a vendor's team did in the backoffice, newest first (its Activity tab):
 * the changes it made, in its own words ("Created the ad campaign “Autumn
 * rugs”"), its sign-ins, the pages it opened, and the credits it was charged —
 * by day, by kind and by person. Changes made by Ardealy support (Act-as) say
 * so.
 */
export function VendorTimelinePanel({ vendorId }: { vendorId: string }) {
  const { t, i18n } = useTranslation();
  const api = useFeatureUsageApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [filter, setFilter] = useState<Filter>('all');
  const [person, setPerson] = useState<string>(EVERYONE);
  const [people, setPeople] = useState<VendorPerson[]>([]);
  const [items, setItems] = useState<VendorActivityItem[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const request = useRef(0);

  const params = useMemo(
    () => ({
      limit: PAGE_SIZE,
      ...(filter !== 'all' ? { kinds: [filter] } : {}),
      ...(person !== EVERYONE ? { user: person } : {}),
    }),
    [filter, person],
  );

  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setFailed(false);
    try {
      const res = await apiRef.current.activity(vendorId, params);
      if (id !== request.current) return;
      setItems(res.items);
      setNext(res.nextBefore);
      setHasMore(res.hasMore);
    } catch {
      if (id === request.current) setFailed(true);
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [vendorId, params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    apiRef.current.people(vendorId).then((p) => setPeople(p || [])).catch(() => setPeople([]));
  }, [vendorId]);

  const loadMore = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const res = await apiRef.current.activity(vendorId, { ...params, before: next });
      setItems((prev) => [...prev, ...res.items]);
      setNext(res.nextBefore);
      setHasMore(res.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  const days = useMemo(() => byDay(toLines(items)), [items]);

  const pageName = (page: string) => {
    const key = vendorPageKey(page);
    return key ? t(`ADMIN_USAGE.PAGES.${key}`) : page;
  };

  const dayLabel = (day: string) => {
    const date = new Date(`${day}T12:00:00`);
    const today = new Date();
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    if (same(date, today)) return t('ADMIN_USAGE.TIMELINE.TODAY');
    if (same(date, yesterday)) return t('ADMIN_USAGE.TIMELINE.YESTERDAY');
    return date.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
  };
  const time = (at: string) => new Date(at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  /** The line's sentence and its details. */
  const words = (line: TimelineLine): { sentence: string; details?: string } => {
    const item = line.item;
    switch (line.kind) {
      case 'change':
        return describeAction({ summary: item.summary, status: item.status, method: item.method, path: item.path, at: item.at } as any, t);
      case 'signin':
        return {
          sentence: t('ADMIN_USAGE.TIMELINE.SIGNED_IN'),
          details: [item.device ? t(`ADMIN_USAGE.TIMELINE.DEVICE.${item.device.toUpperCase()}`, { defaultValue: item.device }) : null, item.browser, item.place].filter(Boolean).join(' · ') || undefined,
        };
      case 'credits': {
        const details = [item.reason, line.charges > 1 ? t('ADMIN_USAGE.TIMELINE.CHARGES', { count: line.charges }) : null].filter(Boolean).join(' · ');
        return line.total < 0
          ? { sentence: t('ADMIN_USAGE.TIMELINE.REFUNDED', { count: -line.total }), details }
          : { sentence: t('ADMIN_USAGE.TIMELINE.CHARGED', { count: line.total }), details };
      }
      case 'pages': {
        // Two routes can be one page (Home): a name opened twice in a row is said once.
        const names = line.pages.map(pageName).filter((name, i, all) => i === 0 || name !== all[i - 1]);
        const shown = names.slice(0, SHOWN_PAGES).join(t('ADMIN_USAGE.TIMELINE.THEN'));
        const rest = names.length - SHOWN_PAGES;
        return { sentence: t('ADMIN_USAGE.TIMELINE.OPENED', { pages: rest > 0 ? `${shown}${t('ADMIN_USAGE.TIMELINE.AND_MORE', { count: rest })}` : shown }) };
      }
    }
  };

  const who = (item: VendorActivityItem) => {
    if (item.via === 'support') return t('ADMIN_USAGE.TIMELINE.SUPPORT', { email: item.actor });
    return item.name || item.actor || t('ADMIN_USAGE.TIMELINE.SOMEONE');
  };

  return (
    <div className="flex flex-col gap-4" data-testid="vendor-timeline">
      <p className="text-sm text-muted-foreground">{t('ADMIN_USAGE.TIMELINE.INTRO')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <PillTabs<Filter>
          value={filter}
          onChange={setFilter}
          ariaLabel={t('ADMIN_USAGE.TIMELINE.FILTER')}
          options={FILTERS.map((f) => ({ value: f, label: t(`ADMIN_USAGE.TIMELINE.KIND.${f.toUpperCase()}`) }))}
        />
        <Select value={person} onValueChange={setPerson}>
          <SelectTrigger className="h-8 w-[220px]" aria-label={t('ADMIN_USAGE.TIMELINE.PERSON')} data-testid="vendor-timeline-person">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EVERYONE}>{t('ADMIN_USAGE.TIMELINE.EVERYONE')}</SelectItem>
            {people.map((p) => (
              <SelectItem key={p.user} value={p.user}>
                {p.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="ms-auto h-8" onClick={load} disabled={loading} aria-label={t('ADMIN_USAGE.REFRESH')}>
          <RefreshCw className={cn(loading && 'animate-spin')} />
          <span className="hidden sm:inline">{t('ADMIN_USAGE.REFRESH')}</span>
        </Button>
      </div>
      {person !== EVERYONE && filter !== 'credits' && <p className="text-xs text-muted-foreground">{t('ADMIN_USAGE.TIMELINE.PERSON_NOTE')}</p>}

      {failed && !items.length ? (
        <Card>
          <EmptyState
            icon={<History />}
            title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
            action={
              <Button variant="outline" size="sm" onClick={load}>
                {t('ADMIN_SHELL.COMMON.RETRY')}
              </Button>
            }
          />
        </Card>
      ) : loading && !items.length ? (
        <Card className="gap-3 p-4" data-testid="vendor-timeline-loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      ) : !items.length ? (
        <Card>
          <EmptyState icon={<History />} title={t('ADMIN_USAGE.TIMELINE.EMPTY_TITLE')} description={t('ADMIN_USAGE.TIMELINE.EMPTY_BODY')} />
        </Card>
      ) : (
        <div className={cn('flex flex-col gap-4', loading && 'opacity-60 transition-opacity')}>
          {days.map(([day, lines]) => (
            <section key={day} className="grid gap-2" data-testid={`vendor-timeline-day-${day}`}>
              <h3 className="text-xs font-medium text-muted-foreground first-letter:uppercase">{dayLabel(day)}</h3>
              <Card className="gap-0 py-0">
                <ol className="divide-y">
                  {lines.map((line) => {
                    const Icon = ICON[line.kind];
                    const { sentence, details } = words(line);
                    return (
                      <li key={line.key} className="flex items-start gap-3 px-4 py-3" data-testid="vendor-timeline-line" data-kind={line.kind}>
                        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <Icon className="size-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm leading-snug">{sentence}</p>
                          {details && <p className="text-xs text-muted-foreground">{details}</p>}
                          {line.kind !== 'credits' && (
                            <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                              <span dir="ltr">{who(line.item)}</span>
                              {line.item.via === 'support' && (
                                <span className="rounded-sm bg-muted px-1 py-px text-[10px] font-medium text-foreground">{t('ADMIN_USAGE.TIMELINE.ACT_AS')}</span>
                              )}
                            </p>
                          )}
                        </div>
                        <time className="shrink-0 text-xs text-muted-foreground tabular-nums" dateTime={line.at} title={new Date(line.at).toLocaleString(i18n.language)}>
                          {(line.kind === 'pages' || line.kind === 'credits') && time(line.since) !== time(line.at) ? `${time(line.since)}–${time(line.at)}` : time(line.at)}
                        </time>
                      </li>
                    );
                  })}
                </ol>
              </Card>
            </section>
          ))}
          {hasMore && (
            <Button variant="outline" className="self-center" onClick={loadMore} disabled={loadingMore} data-testid="vendor-timeline-more">
              {loadingMore && <Loader2 className="animate-spin" />}
              {t('ADMIN_USAGE.TIMELINE.LOAD_MORE')}
            </Button>
          )}
          <p className="text-center text-xs text-muted-foreground">{t('ADMIN_USAGE.TIMELINE.SINCE_NOTE')}</p>
        </div>
      )}
    </div>
  );
}
